/**
 * Published apps: the catalogue, the ones you own, and the one you are looking at.
 *
 * Web-owned because the shared myAppsStore fetches its token from CognitoService, which
 * since SEC-14 reaches expo-secure-store and therefore React Native.
 */
import type { AppDefinition } from '@/types/app';
import type { AppListing } from '@/backend/types';
import { ApiError, request } from './client';

/** Everything published and listed. Unlisted apps never appear here. */
export async function fetchCatalog(): Promise<AppListing[]> {
  const body = await request<{ items?: AppListing[] }>('/apps');
  return body.items ?? [];
}

/** The apps this person owns or has joined. */
export async function fetchMyApps(): Promise<AppListing[]> {
  const body = await request<{ items?: AppListing[] }>('/me/apps');
  return body.items ?? [];
}

/** One app. Null rather than a throw when it simply is not there. */
export async function fetchApp(appId: string): Promise<AppListing | null> {
  try {
    return await request<AppListing>(`/apps/${encodeURIComponent(appId)}`);
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}

export async function enrollInApp(appId: string): Promise<void> {
  await request(`/apps/${encodeURIComponent(appId)}/enroll`, { method: 'POST' });
}

/**
 * The definition behind a published app, for remixing it.
 *
 * Null when the app has none to give — a remix of nothing should offer nothing, rather
 * than create an empty project the person then has to delete.
 */
export async function fetchPublishedDefinition(appId: string): Promise<AppDefinition | null> {
  try {
    const app = await request<{ definition?: AppDefinition }>(`/apps/${encodeURIComponent(appId)}`);
    return app.definition ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.kind === 'not_found') return null;
    throw err;
  }
}
